import React, { useMemo, useState, useEffect } from 'react';
import { Container } from '@common/components/container';
import { TopNavigationBar } from '@common/components/navigation-bar';
import { Button } from '@common/components/button';
import { useHistory, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CardContent } from '@common/components/card/card-content';
import { Toggle } from '@common/components/form/inputs/toggle';
import Card from '@common/components/card';
import { Group, Row, SelectInputHookForm } from '@common/components/form';
import * as organisationSelectors from '../../../organisation/selectors/organisation';
import { TextInputHookForm } from '@common/components/form/inputs/text';
import { FormLabelHookForm } from '@common/components/form/label';
import { TextareaInputHookForm as TextareaInput, TextareaInputHookForm } from '@common/components/form/inputs/textarea/textarea';
import Icon from '@common/components/icon';
import { useAppSelector } from '@common/hooks';
import { OverviewSection } from '@common/components/overview';
import { HookFormWrapper } from '@common/hooks/use-hook-form';
import { createCompetancy } from '../../action/create-competancy';
import { updateCompetancy } from '../../action/update-competancy';
import {
  CategoryPayload,
  CompetancyPayload,
  RatingScalePayload,
} from '../../types';
import { AlertService } from '@common/services/alert';
import { selected } from '@modules/organisation/selectors/organisation';
import { useRequest } from '@common/services/api/useRequest';
import Spinner from '@common/components/spinner';
import Placeholder from '@common/components/placeholder';
// import { EPredicateFields } from '@common/definitions';
// import { EPredicateFilters } from '@common/components/predicates-filter';
// import FiltersToolbar from '@common/components/filters-toolbar/filters-toolbar';
import { createCategory } from '@modules/performance/action/create-category';

type FormData = {
  name: string;
  category: { label: string; value: string } | string;
  description: string;
  functions: Array<{ label: string; value: string }>;
  customRating: boolean;
  improvement: string;
  ratingScales: RatingScalePayload[];
};

const CompetancyForm = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const functions = useAppSelector(organisationSelectors.functions);
  const organisation = useAppSelector(selected);
  const { networkId, id } = useParams<{ networkId: string; id: string }>();
  const basePath = networkId ? `/networks/${networkId}` : '/admin';
  // const languages = useAppSelector((state) => state.language.available);
  // const defaultLanguage = useAppSelector((state) => state.loggedUser.user.language_locale);
  const [originalRatingScales, setOriginalRatingScales] = useState<
    RatingScalePayload[]
  >([]);

  // const [languagePredicates, setLanguagePredicates] = useState([
  //   {
  //     filter: EPredicateFilters[EPredicateFields.COMPETENCY_LANGUAGE],
  //     value: {
  //       attribute: EPredicateFields.COMPETENCY_LANGUAGE,
  //       comparison: 'eq' as const,
  //       value: defaultLanguage,
  //     },
  //     options: languages || [],
  //   },
  // ]);

  // const [language] = useMemo(() => {
  //   return [
  //     languagePredicates.find(
  //       (p) => p.filter === EPredicateFilters[EPredicateFields.COMPETENCY_LANGUAGE]
  //     )?.value.value,
  //   ];
  // }, [languagePredicates]);

  const { data: ratingScales } = useRequest(
    `/v1/organisations/${organisation.id}/performance/rating-scales`,
    // { language: language || '' }
  );

  const { data: categories, setData: setCategories } = useRequest(
    `/v1/organisations/${organisation.id}/performance/competency/categories`,
    // { language: language || '' }
  );

  const { data: competencyData, loading } = useRequest(
    `/v1/organisations/${organisation.id}/performance/competencies/${id}`,
    // { language: language || '' }
  );

  // useEffect(() => {
  //   refetchCompetency();
  //   refetchCategories();
  //   refetchRatingScales();
  // }, [language]);

  const competency = Array.isArray(competencyData)
    ? competencyData[0]
    : competencyData;

  useEffect(() => {
    if (competency?.rating_scales) {
      setOriginalRatingScales(competency.rating_scales);
    } else if (ratingScales && Array.isArray(ratingScales)) {
      setOriginalRatingScales(ratingScales);
    }
  }, [competency?.rating_scales, ratingScales]);

  const hasAnyOverrides = useMemo(() => {
    if (!Array.isArray(ratingScales) || !Array.isArray(competency?.rating_scales)) {
      return false;
    }

    const baseById = new Map<string | undefined, RatingScalePayload>(
      (ratingScales as RatingScalePayload[]).map((scale) => [scale.id, scale])
    );
    return (competency?.rating_scales as RatingScalePayload[] | undefined || []).some((compScale) => {
      const baseScale = baseById.get(compScale.id);
      return (
        !!compScale?.description &&
        !!baseScale &&
        compScale.description !== baseScale.description
      );
    });
  }, [ratingScales, competency?.rating_scales]);

  const isRatingScaleModified = useMemo(() => {
    return (currentScale: RatingScalePayload, index: number) => {
      if (!currentScale.id) return false;
      const originalScale = originalRatingScales[index];
      if (!originalScale) return false;
      const isModified = currentScale.description !== originalScale.description;
      return isModified;
    };
  }, [originalRatingScales]);

  const onSubmit = async (formData: FormData): Promise<void> => {
    if (!organisation?.id) {
      AlertService.error(t('common:organisation_not_found'));
      return;
    }

    let { ratingScales: ratingScalesData } = formData;
    const modifiedScales =
      ratingScalesData?.filter((scale: RatingScalePayload, index: number) => {
        return isRatingScaleModified(scale, index);
      }) || [];

    if (competency?.id) {
      ratingScalesData = modifiedScales.map((scale: RatingScalePayload) => ({
        // locale: language,
        scaleId: scale.id,
        description: scale.description,
      }));
    } else {
      ratingScalesData = modifiedScales.map((scale: RatingScalePayload) => ({
        // locale: language,
        scaleId: scale.id,
        description: scale.description,
      }));
    }

    try {
      const payload: CompetancyPayload = {
        // locale: language,
        name: formData.name,
        description: formData.description,
        categoryId:
          typeof formData.category === 'string'
            ? formData.category
            : formData.category.value,
        functionIds: formData.functions.map((f) => f.value),
        ratingScales: ratingScalesData,
      };

      if (competency?.id) {
        await updateCompetancy(organisation.id, competency.id, payload);
        AlertService.success(t('performance:competency_updated_successfully'));
      } else {
        const updatedPayload = {
          ...payload,
          ratingScales: ratingScalesData?.map((scale: any) => ({
            // locale: language,
            scaleId: scale.scaleId,
            description: scale.description,
            competencyId: competency?.id,
          })),
        };
        await createCompetancy(organisation.id, updatedPayload);
        AlertService.success(t('performance:competency_created_successfully'));
        history.push(
          `${basePath}/performance/competency-library/all`
        );
      }
    } catch (error: any) {
      AlertService.forStatus(error.status_code, {
        warning: t('performance:competency_create_warning'),
        error: t('performance:competency_create_error'),
      });
    }
  };

  return (
    <Container name="CompetancyForm">
      <HookFormWrapper<FormData>
        initialValues={{
          name: competency?.name || '',
          category: competency?.category_id
            ? {
                label: competency.category?.name || '',
                value: competency.category_id,
              }
            : '',
          description: competency?.description || '',
          functions:
            competency?.functions?.map((f: any) => ({
              label: f.name,
              value: f.id,
            })) || [],
          customRating: hasAnyOverrides,
          improvement: '',
          ratingScales:
            competency?.rating_scales?.length > 0
              ? competency.rating_scales
              : Array.isArray(ratingScales)
              ? ratingScales
              : [],
        }}
        onSubmit={onSubmit}
        mode="onBlur"
        key={competency?.id || 'new'}
      >
        {({
          control,
          handleSubmit,
          formState: { isSubmitting, isDirty },
          watch,
          setValue,
        }) => {
          const ratingScale = watch('ratingScales');

          const handleCreateCategory = async (text: string) => {
            try {
              const payload: CategoryPayload = {
                name: text.trim(),
              };

              const response = await createCategory(organisation.id, payload);

              if (response.data) {
                const newCategoryData = {
                  id: response.data.id,
                  name: text.trim(),
                };

                setCategories((prevCategories) => (prevCategories
                    ? [newCategoryData, ...prevCategories]
                    : [newCategoryData])
                );

                AlertService.success(
                  t('performance:category_created_successfully')
                );

                const newCategory = {
                  label: text.trim(),
                  value: response.data.id,
                };

                setTimeout(() => {
                  setValue('category', newCategory, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }, 50);
              }
            } catch (error: any) {
              AlertService.forStatus(error.status_code, {
                warning: t('performance:category_create_warning'),
                error: t('performance:category_create_error'),
              });
            }
          };

          return (
            <>
              <TopNavigationBar
                breadcrumbs={[
                  { name: t('performance:tab_performance') },
                  {
                    name: t('performance:competency_library'),
                    path: `${basePath}/performance/competency-library/all`,
                  },
                  {
                    name: competency?.name
                      ? competency.name
                      : t('performance:new_competency'),
                  },
                ]}
                title={
                  competency?.name
                    ? competency.name
                    : t('performance:new_competency')
                }
                action={
                  <>
                    {/* <FiltersToolbar
                      predicates={languagePredicates}
                      setPredicates={setLanguagePredicates}
                      organisation={organisation}
                      searchEnabled={false}
                      className="!tw-w-auto"
                    /> */}
                    {/* <ConfirmButton
                      title={t('learning:gen_ai_unsaved_changes_title')}
                      description={t(
                        'learning:gen_ai_unsaved_changes_description'
                      )}
                      onCancel={() => {}}
                      onConfirm={() => history.push(
                          `${basePath}/performance/competency-library/all`
                        )}
                      confirmText={t('learning:gen_ai_unsaved_changes_confirm')}
                      cancelText={t('learning:gen_ai_unsaved_changes_cancel')}
                      danger
                    > */}
                    <Button
                      size="large"
                      onClick={() => {
                        history.push(
                          `${basePath}/performance/competency-library/all`
                        );
                      }}
                    >
                      {t('performance:go_back')}
                    </Button>
                    {/* </ConfirmButton> */}
                    <Button
                      onClick={handleSubmit(onSubmit)}
                      type="primary"
                      isLoading={isSubmitting}
                      disabled={isSubmitting || !isDirty}
                    >
                      {competency?.id
                        ? t('common:save_changes')
                        : t('performance:add_competency')}
                    </Button>
                  </>
                }
              />
              <Container.Content className="tw-gap-6 !tw-w-[650px]">
                {loading ? (
                  <Spinner centered size="large" />
                ) : (
                  <>
                    <Card containerClassName="tw-w-full !tw-p-0 !tw-rounded-lg">
                      <CardContent className="!tw-w-[500px] !tw-w-[90%] !tw-p-8 !tw-gap-5 !tw-flex !tw-flex-col">
                        <Row className="!tw-mb-0">
                          <Group>
                            <FormLabelHookForm
                              control={control}
                              name="name"
                              text={t('performance:name')}
                              className="!tw-text-[13px] !tw-font-inter !tw-font-normal !tw-text-gray-text"
                            />
                            <TextInputHookForm
                              name="name"
                              control={control}
                              placeholder={t(
                                'performance:enter_competency_name'
                              )}
                              className="!tw-shadow-input"
                            />
                          </Group>
                        </Row>
                        <Row className="!tw-mb-0">
                          <Group>
                            <FormLabelHookForm
                              control={control}
                              name="category"
                              text={t('performance:category')}
                              className="!tw-text-[13px] !tw-font-inter !tw-font-normal !tw-text-gray-text"
                            />
                            <SelectInputHookForm
                              key={`category-select-${categories.length}`}
                              name="category"
                              control={control}
                              searchable
                              placeholder={t('performance:select_category')}
                              className="!tw-shadow-input"
                              options={
                                categories?.map((c) => ({
                                  label: c.name,
                                  value: c.id,
                                })) || []
                              }
                              onNewOptionClick={(e: any) => {
                                handleCreateCategory(e.label);
                              }}
                            />
                          </Group>
                        </Row>
                        <Row className="!tw-mb-0">
                          <Group>
                            <FormLabelHookForm
                              control={control}
                              name="description"
                              text={t('performance:description')}
                              className="!tw-text-[13px] !tw-font-inter !tw-font-normal !tw-text-gray-text"
                            />
                            <TextareaInput
                              className="!tw-h-[100px] !tw-shadow-input"
                              name="description"
                              control={control}
                              placeholder={t('performance:description')}
                              minRows={3}
                            />
                            <div
                              className="tw-flex tw-border-l tw-border-r tw-border-b tw-border-gray-neutral-50
                            tw-rounded-b-lg tw-flex-row tw-items-center tw-gap-2 tw-py-2 tw-px-3 tw-bg-gray-neutral-50"
                            >
                              <div className="tw-flex tw-flex-row">
                                <Icon
                                  type="info"
                                  size={16}
                                  className="tw-text-gray-muted tw-mr-2"
                                />
                                <div className="tw-text-gray-text tw-items-center tw-text-sm-plus tw-font-normal">
                                  {t('performance:description_tooltip')}
                                </div>
                              </div>
                            </div>
                          </Group>
                        </Row>
                        <Row className="!tw-mb-0">
                          <Group>
                            <FormLabelHookForm
                              control={control}
                              name="functions"
                              text={t('performance:functions')}
                              className="!tw-text-[13px] !tw-font-inter !tw-font-normal !tw-text-gray-text"
                            />
                            <SelectInputHookForm
                              name="functions"
                              multi
                              control={control}
                              className="!tw-shadow-input"
                              placeholder={t('performance:functions')}
                              options={functions.map((f) => ({
                                label: f.name,
                                value: f.id,
                              }))}
                            />
                          </Group>
                        </Row>
                      </CardContent>
                    </Card>

                    <Card containerClassName="tw-w-full !tw-p-0 !tw-rounded-lg">
                      <CardContent className="!tw-w-[500px] !tw-w-[90%] !tw-p-8 !tw-gap-5 !tw-flex !tw-flex-col">
                        <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                          <Toggle
                            value={watch('customRating')}
                            onChange={(value) => setValue('customRating', value)}
                          />
                          <div className="tw-flex tw-flex-row tw-gap-1">
                            <div className="tw-text-base tw-font-medium tw-text-darkbg">
                              {t('performance:add_custom_rating_descriptions')}
                            </div>
                            <Icon
                              type="info"
                              size={16}
                              className="tw-text-gray-muted"
                            />
                          </div>
                        </div>

                        {watch('customRating') && (
                          <div className="tw-flex tw-flex-col tw-gap-4">
                            {(Array.isArray(ratingScale)
                              ? ratingScale
                              : []
                            ).map((scale, index) => (
                              <OverviewSection
                                key={scale.id || index}
                                className="tw-flex tw-flex-col tw-gap-4"
                              >
                                <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                                  <div
                                    className="tw-text-base tw-font-medium tw-text-white tw-rounded-lg
                                    tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center"
                                    style={{ backgroundColor: scale.color }}
                                  >
                                    {index + 1}
                                  </div>
                                  <div className="tw-text-base tw-font-medium tw-text-gray-textDark">
                                    {scale.label || `Rating ${index + 1}`}
                                  </div>
                                </div>
                                <TextareaInputHookForm
                                  name={`ratingScales.${index}.description`}
                                  rows={3}
                                  className="!tw-shadow-input"
                                  control={control}
                                  placeholder={t(
                                    'performance:custom_description_for_rating'
                                  )}
                                  defaultValue={scale.description || ''}
                                />
                              </OverviewSection>
                            ))}
                            {(!ratingScale || ratingScale.length === 0) && (
                              <Placeholder
                                title={t(
                                  'performance:no_rating_scales_found_description'
                                )}
                              />
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </>
                )}
              </Container.Content>
            </>
          );
        }}
      </HookFormWrapper>
    </Container>
  );
};

export default CompetancyForm;
